import { useState, useEffect } from "react";
import api from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FolderOpen, Image, X } from "lucide-react";

export default function MediaGalleryPage() {
  const [folders, setFolders] = useState([]);
  const [allMedia, setAllMedia] = useState([]);
  const [activeFolder, setActiveFolder] = useState("all");
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  usePageTitle("Media Gallery");

  useEffect(() => {
    Promise.all([
      api.get("/content/media_folders"),
      api.get("/content/media_gallery"),
    ]).then(([f, m]) => {
      setFolders(f.data);
      setAllMedia(m.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filteredMedia = activeFolder === "all"
    ? allMedia
    : allMedia.filter(m => m.folder_id === activeFolder);

  return (
    <div data-testid="media-gallery-page">
      <div className="bg-jci-black py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">Media Gallery</h1>
          <p className="text-white/50 mt-3 text-base">Photos and videos from JCI Bangladesh events</p>
        </div>
        <div className="h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy mt-8" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
        {/* Folder filter */}
        <div className="flex flex-wrap gap-3 mb-10" data-testid="folder-filters">
          <Button
            variant={activeFolder === "all" ? "default" : "outline"}
            onClick={() => setActiveFolder("all")}
            className={activeFolder === "all" ? "bg-jci-blue hover:bg-jci-navy text-white" : ""}
            data-testid="filter-all"
          >
            All Photos
          </Button>
          {folders.map(folder => (
            <Button
              key={folder.id}
              variant={activeFolder === folder.id ? "default" : "outline"}
              onClick={() => setActiveFolder(folder.id)}
              className={activeFolder === folder.id ? "bg-jci-blue hover:bg-jci-navy text-white" : ""}
              data-testid={`filter-folder-${folder.id}`}
            >
              <FolderOpen className="w-4 h-4 mr-1.5" /> {folder.name}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1,2,3,4,5,6,7,8].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}
          </div>
        ) : filteredMedia.length === 0 ? (
          <div className="text-center py-20">
            <Image className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400">No media items in this category yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
            {filteredMedia.map((item, i) => (
              <button
                key={item.id}
                onClick={() => setLightbox(item)}
                className="relative overflow-hidden rounded-xl group aspect-square"
                data-testid={`media-item-${i}`}
              >
                <img
                  src={item.thumbnail_url || item.url}
                  alt={item.title || ""}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-jci-black/0 group-hover:bg-jci-black/30 transition-colors flex items-end">
                  {item.title && (
                    <div className="p-3 w-full opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-white text-sm font-medium truncate">{item.title}</p>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black/95 border-0">
          {lightbox && (
            <div className="relative">
              <img src={lightbox.url || lightbox.thumbnail_url} alt={lightbox.title || ""} className="w-full h-auto max-h-[80vh] object-contain" />
              {lightbox.title && (
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                  <p className="text-white text-sm font-medium">{lightbox.title}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
